import { Stack } from "@mui/material";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { toDays } from "@smartrr/shared/utils/renderViewDate";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { Infinite, ShippingBoxV2 } from "akar-icons";
import React, { useCallback, useState } from "react";

import { ModalComponent } from "@customer-app/account/views/OperationAquarius/GlobalModal";
import { useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";

import { ModalSelectionButton } from "./components/ModalSelectionButton";
import { ISelectModalViewContentProps, ISelectModalViewProps } from "./types";
import { useFrequencyString } from "../../utils";
import { useBasicSubscriptionInfo } from "../ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";

export const SelectModalViewModal = ({ deliveryId, ...props }: ISelectModalViewProps) => {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return <SelectModalViewContentModal {...props} delivery={delivery} />;
};

const SelectModalViewContentModal = ({ delivery, ...props }: ISelectModalViewContentProps) => {
  const { schedule } = delivery.purchaseState;
  const { isPaused } = useBasicSubscriptionInfo(delivery);

  const frequencyString = useFrequencyString(schedule);

  const isNextOrderAvailable = !isPaused;

  const [activeSelection, setActiveSelection] = useState<ModalComponent>(
    isNextOrderAvailable ? "ORDER_OPTIONS" : "MANAGE_SUBSCRIPTIONS"
  );
  const isMobile = useMobileQuery();

  const toggleModal = useToggleModal();

  const onSelect = useCallback((type: ModalComponent) => setActiveSelection(type), []);

  const onConfirm = useCallback(() => toggleModal(activeSelection, { delivery, ...props }), [activeSelection]);

  const onClose = useCallback(
    () => toggleModal(activeSelection, { delivery, ...props }, true),
    [activeSelection]
  );

  return (
    <Stack>
      <Stack
        direction={isMobile ? "column" : "row"}
        padding={isMobile ? "20px 0 0 0" : "30px 50px"}
        justifyContent="center"
        columnGap="50px"
        rowGap="20px"
      >
        {isNextOrderAvailable ? (
          <ModalSelectionButton
            Icon={ShippingBoxV2}
            onClick={onSelect}
            title={typedLocalize("selectModal.nextOrderTitle")}
            subtitle={typedLocalize("utils.dates.toViewDateLongDay", {
              date: delivery?.date,
            })}
            type="ORDER_OPTIONS"
            activeSelection={activeSelection}
          />
        ) : null}
        <ModalSelectionButton
          Icon={Infinite}
          onClick={onSelect}
          title={typedLocalize("selectModal.upcomingOrderTitle")}
          subtitle={typedLocalize("subscriptions.yourSubscriptions.every", {
            deliveryFrequency: toDays(frequencyString),
          })}
          type="MANAGE_SUBSCRIPTIONS"
          activeSelection={activeSelection}
        />
      </Stack>
      <ConfirmCancelButtons onCancel={onClose} onConfirm={onConfirm} />
    </Stack>
  );
};
